import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Field } from 'formik';
import classNames from 'classnames';

import { displayMoney } from '../../../util/currency';
import { incotermsList } from '../../../util/incoterms';

const SectionDescriptionStyled = styled.div`
  width: 570px;
  margin-bottom: 40px;
`;

const CheckboxStyled = styled.input`
  display: inline-flex;
`;

const CheckboxLabelStyled = styled.label`
  margin-left: 7px;
`;

class QuoteFormSectionQuantities extends React.Component {
  static defaultProps = {
    readOnly: false,
  };

  static propTypes = {
    readOnly: PropTypes.bool,
    product: PropTypes.object.isRequired,
  };

  render() {
    const {
      readOnly,
      product,
      values,
      errors,
      touched,
      setFieldValue,
      setFieldTouched,
      setValues,
    } = this.props;
    return (
      <fieldset>
        {product.targetPrice && (
          <div data-id="quote-form-target-required-quantity">
            <SectionDescriptionStyled>
              Please review the target price the customer has requested and give an estimate for the
              quantity required to reach this price.
            </SectionDescriptionStyled>
            <div className={'row'}>
              <div className="col-sm-4">
                <div className="form-group">
                  <label>Target Price</label>
                  <div id="targetPrice">{displayMoney(product.targetPrice, product.currency)}</div>
                </div>
              </div>
            </div>
            <div className={'row'}>
              <div className="col-sm-5">
                <div
                  className={classNames('form-group', {
                    'with-error': touched.targetRequiredQuantity && errors.targetRequiredQuantity,
                  })}
                >
                  <label>Required Quantity to hit this price?</label>
                  <Field
                    name="targetRequiredQuantity"
                    className="form-control"
                    type="number"
                    disabled={
                      values.targetPriceCanNotReach ||
                      (values.minimumQuantity === values.targetRequiredQuantity &&
                        values.pricePerUnit === product.targetPrice) ||
                      readOnly
                    }
                    onChange={(event) => {
                      const targetRequiredQuantity = event.target.value;
                      const data = { ...values, targetRequiredQuantity: targetRequiredQuantity };
                      Object.keys(data.requiredQuantity).forEach((i) => {
                        if (data.requiredQuantity[i].q === parseInt(targetRequiredQuantity, 10)) {
                          data.requiredQuantity[i].pricePerUnit = product.targetPrice;
                          data.requiredQuantity[i].leadTime = data.targetLeadTime;
                        } else if (
                          data.requiredQuantity[i].q < parseInt(targetRequiredQuantity, 10)
                        ) {
                          data.requiredQuantity[i].pricePerUnit = '';
                          data.requiredQuantity[i].leadTime = '';
                        }
                      });
                      if (
                        values.minimumQuantity === targetRequiredQuantity &&
                        values.pricePerUnit === product.targetPrice
                      ) {
                        data.targetLeadTime = values.leadTime;
                      }
                      setValues(data);
                    }}
                    onBlur={() => {
                      setFieldTouched('targetRequiredQuantity', true);
                    }}
                  />
                  {touched.targetRequiredQuantity &&
                    errors.targetRequiredQuantity && (
                      <label id="targetRequiredQuantity-error" className="error">
                        {errors.targetRequiredQuantity}
                      </label>
                    )}
                </div>
              </div>
              <div className="col-sm-5">
                <div
                  className={classNames('form-group', {
                    'with-error': touched.targetLeadTime && errors.targetLeadTime,
                  })}
                >
                  <label>Lead Time</label>
                  <Field
                    className="form-control narrow"
                    name={`targetLeadTime`}
                    placeholder="ex. 30 Days"
                    disabled={
                      values.targetPriceCanNotReach ||
                      (values.minimumQuantity === values.targetRequiredQuantity &&
                        values.pricePerUnit === product.targetPrice) ||
                      readOnly
                    }
                    onChange={(event) => {
                      const v = event.target.value;
                      const data = { ...values, targetLeadTime: v };
                      Object.keys(data.requiredQuantity).forEach((i) => {
                        if (data.requiredQuantity[i].q === parseInt(v, 10)) {
                          data.requiredQuantity[i].leadTime = v;
                        }
                      });
                      setValues(data);
                    }}
                    onBlur={() => {
                      setFieldTouched('targetLeadTime', true);
                    }}
                  />
                  {touched.targetLeadTime &&
                    errors.targetLeadTime && (
                      <label className="error">{errors.targetLeadTime}</label>
                    )}
                </div>
              </div>
            </div>
            <div className={'row'}>
              <div className="col-sm-6">
                <div className="form-group">
                  <CheckboxStyled
                    id="targetPriceCanNotReach"
                    name="targetPriceCanNotReach"
                    type="checkbox"
                    onChange={() => {
                      setFieldValue('targetPriceCanNotReach', !values.targetPriceCanNotReach);
                    }}
                    onBlur={() => {
                      setFieldTouched('targetPriceCanNotReach', true);
                    }}
                    checked={values.targetPriceCanNotReach}
                    disabled={readOnly}
                  />
                  <CheckboxLabelStyled htmlFor="targetPriceCanNotReach">
                    Select here if you cannot reach this price
                  </CheckboxLabelStyled>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="section-header">Volume Quotes</div>
        <div className="section-header-description">
          <em>Please provide the Minimum Order Quantity, price, and lead time for this product.</em>
        </div>
        <div className="row">
          <div
            className={classNames('form-group col-4 col-sm-4', {
              'with-error': touched.minimumQuantity && errors.minimumQuantity,
            })}
          >
            <label>Minimum Quantity</label>
            <div className="form-value">
              <input
                className="form-control narrow"
                type="number"
                maxLength="15"
                name="minimumQuantity"
                placeholder="ex. 1,000"
                onChange={(event) => {
                  const v = event.target.value;
                  const data = { ...values, minimumQuantity: v };
                  Object.keys(data.requiredQuantity).forEach((i) => {
                    if (data.requiredQuantity[i].q === parseInt(v, 10)) {
                      data.requiredQuantity[i].pricePerUnit = data.pricePerUnit;
                      data.requiredQuantity[i].leadTime = data.leadTime;
                    }
                    if (data.requiredQuantity[i].q < parseInt(v, 10)) {
                      data.requiredQuantity[i].pricePerUnit = '';
                      data.requiredQuantity[i].leadTime = '';
                    }
                  });
                  setValues(data);
                }}
                onBlur={() => {
                  setFieldTouched('minimumQuantity', true);
                }}
                value={values.minimumQuantity}
                disabled={readOnly}
              />
              {touched.minimumQuantity &&
                errors.minimumQuantity && <label className="error">{errors.minimumQuantity}</label>}
            </div>
          </div>
          <div
            className={classNames('form-group col-4 col-sm-4', {
              'with-error': touched.pricePerUnit && errors.pricePerUnit,
            })}
          >
            <label>Price per Unit</label>
            <div className="form-value">
              <input
                className="form-control narrow"
                type="number"
                name="pricePerUnit"
                placeholder="ex. $4.56"
                onChange={(event) => {
                  const v = event.target.value;
                  const data = { ...values, pricePerUnit: v };
                  Object.keys(data.requiredQuantity).forEach((i) => {
                    if (data.requiredQuantity[i].q === parseInt(data.minimumQuantity, 10)) {
                      data.requiredQuantity[i].pricePerUnit = v;
                    }
                  });
                  setValues(data);
                }}
                onBlur={() => {
                  setFieldTouched('pricePerUnit', true);
                }}
                value={values.pricePerUnit}
                disabled={readOnly}
              />
              {touched.pricePerUnit &&
                errors.pricePerUnit && <label className="error">{errors.pricePerUnit}</label>}
            </div>
          </div>
          <div
            className={classNames('form-group col-4 col-sm-4', {
              'with-error': touched.leadTime && errors.leadTime,
            })}
          >
            <label>Lead Time</label>
            <div className="form-value">
              <input
                className="form-control narrow"
                type="text"
                name="leadTime"
                placeholder="ex. 30 Days"
                onChange={(event) => {
                  const v = event.target.value;
                  const data = { ...values, leadTime: v };
                  Object.keys(data.requiredQuantity).forEach((i) => {
                    if (data.requiredQuantity[i].q === parseInt(data.minimumQuantity, 10)) {
                      data.requiredQuantity[i].leadTime = v;
                    }
                  });
                  setValues(data);
                }}
                onBlur={() => {
                  setFieldTouched('leadTime', true);
                }}
                value={values.leadTime}
                disabled={readOnly}
              />
              {touched.leadTime &&
                errors.leadTime && <label className="error">{errors.leadTime}</label>}
            </div>
          </div>
        </div>

        <div className="section-header" />
        <div className="section-header-description">
          <em>
            Please provide a price per unit and the estimated lead time for each volume level.
          </em>
        </div>
        <div className="row">
          <div className="form-group col-4 col-sm-4">
            <label>Required Quantity</label>
          </div>
          <div className="form-group col-4 col-sm-4">
            <label>Price per Unit</label>
          </div>
          <div className="form-group col-4 col-sm-4">
            <label>Lead Time</label>
          </div>
        </div>
        {values.requiredQuantity.map((item, index) => {
          const quantity = item.q;
          const fieldTouched = (touched.requiredQuantity && touched.requiredQuantity[index]) || {};
          const fieldError = (errors.requiredQuantity && errors.requiredQuantity[index]) || {};
          const fieldDisabled =
            (values.minimumQuantity && values.minimumQuantity >= quantity) || readOnly;

          return (
            <div className="row" key={index}>
              <div className="form-group col-4 col-sm-4">
                <div className="form-value vertically-centered required-quantity">{quantity}</div>
              </div>
              <div
                className={classNames('form-group col-4 col-sm-4', {
                  'with-error': fieldTouched.pricePerUnit && fieldError.pricePerUnit,
                })}
              >
                <div className="form-value">
                  <Field
                    className="form-control narrow"
                    type="number"
                    name={`requiredQuantity.${index}.pricePerUnit`}
                    placeholder="ex. $4.56"
                    disabled={fieldDisabled}
                  />
                  {fieldTouched.pricePerUnit &&
                    fieldError.pricePerUnit && (
                      <label className="error">{fieldError.pricePerUnit}</label>
                    )}
                </div>
              </div>
              <div
                className={classNames('form-group col-4 col-sm-4', {
                  'with-error': fieldTouched.leadTime && fieldError.leadTime,
                })}
              >
                <div className="form-value">
                  <Field
                    className="form-control narrow"
                    name={`requiredQuantity.${index}.leadTime`}
                    placeholder="ex. 30 Days"
                    disabled={fieldDisabled}
                  />
                  {fieldTouched.leadTime &&
                    fieldError.leadTime && <label className="error">{fieldError.leadTime}</label>}
                </div>
              </div>
            </div>
          );
        })}

        <div className="section-header">Incoterms</div>
        <div className="section-header-description">
          <em>
            The customer has specified their preferred Incoterms, please indicate your preference.
          </em>
        </div>
        <div className="row">
          <div className="form-group col-6 col-sm-4">
            <label>Requested Terms</label>
          </div>
          <div className="form-group col-6 col-sm-4">
            <label>Terms Preference</label>
          </div>
        </div>
        <div className="row form-group">
          <div className="form-group col-6 col-sm-4">
            <div className="form-value vertically-centered">
              {incotermsList[product.incoterms].label}
            </div>
          </div>
          <div className="form-group col-6 col-sm-4">
            <div className="form-value">
              <Field
                className="form-control narrow"
                component="select"
                name="termsPreference"
                disabled={readOnly}
              >
                {Object.values(incotermsList)
                  .filter((incoterm) => incoterm.code !== 'standard')
                  .map((incoterm) => {
                    return (
                      <option value={incoterm.code} key={incoterm.code}>
                        {incoterm.label}
                      </option>
                    );
                  })}
              </Field>
            </div>
          </div>
        </div>

        <div className="section-header">Shipping Information</div>
        <div
          className={classNames('form-group', {
            'with-error': touched.cartonSize && errors.cartonSize,
          })}
        >
          <label>Carton Size</label>
          <Field
            className="form-control"
            name="cartonSize"
            placeholder="Please confirm the carton size (A x B x C cm)"
            disabled={readOnly}
          />
          {touched.cartonSize &&
            errors.cartonSize && <label className="error">{errors.cartonSize}</label>}
        </div>
        <div
          className={classNames('form-group', {
            'with-error': touched.quantityPerCarton && errors.quantityPerCarton,
          })}
        >
          <label>Quantity per Carton</label>
          <Field
            className="form-control"
            type="number"
            name="quantityPerCarton"
            placeholder="Please confirm how many units can fit in 1 carton."
            disabled={readOnly}
          />
          {touched.quantityPerCarton &&
            errors.quantityPerCarton && <label className="error">{errors.quantityPerCarton}</label>}
        </div>
        <div
          className={classNames('form-group', {
            'with-error': touched.cartonGrossWeight && errors.cartonGrossWeight,
          })}
        >
          <label>Carton Gross Weight (Kg)</label>
          <Field
            className="form-control"
            type="number"
            name="cartonGrossWeight"
            placeholder="Please confirm the carton gross weight."
            disabled={readOnly}
          />
          {touched.cartonGrossWeight &&
            errors.cartonGrossWeight && <label className="error">{errors.cartonGrossWeight}</label>}
        </div>
        <div
          className={classNames('form-group', {
            'with-error': touched.cartonNetWeight && errors.cartonNetWeight,
          })}
        >
          <label>Carton Net Weight (Kg)</label>
          <Field
            className="form-control"
            type="number"
            name="cartonNetWeight"
            placeholder="Please confirm the carton net weight."
            disabled={readOnly}
          />
          {touched.cartonNetWeight &&
            errors.cartonNetWeight && <label className="error">{errors.cartonNetWeight}</label>}
        </div>
      </fieldset>
    );
  }
}

export default QuoteFormSectionQuantities;



// WEBPACK FOOTER //
// ./src/component/quote/QuoteFormSectionQuantities/index.js